// apps/dashboard/src/components/chrome/PublicShell.tsx
//
// Chrome for the public, unauthenticated demo scan page (/public/scans/...).
// No sidebar — a website-style top nav instead. The nav is intentionally a
// lightweight duplicate of apps/website's <Nav>: the two apps own separate
// design systems and never share components, so mirroring a handful of links
// here is the sanctioned pattern, not a candidate for extraction.
import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { LogoMark, buttonClasses } from "@/design-system";
import { track } from "@/lib/analytics";

const WEBSITE_URL =
  (import.meta.env.VITE_WEBSITE_URL as string | undefined) ??
  "https://gated.cc";

interface PublicShellProps {
  children: ReactNode;
}

export function PublicShell({ children }: PublicShellProps) {
  return (
    <div className="h-screen bg-bg-2 flex flex-col">
      <PublicNav />
      <main className="flex-1 overflow-hidden bg-bg-2 flex flex-col">
        {children}
      </main>
    </div>
  );
}

function PublicNav() {
  return (
    <header className="shrink-0 border-b border-border-1 bg-bg-1">
      <div className="mx-auto flex h-13 max-w-7xl flex-row items-center justify-between px-6">
        <a
          href={WEBSITE_URL}
          className="flex flex-row items-center gap-2 text-fg-1"
          aria-label="Gated home"
        >
          <LogoMark size={20} />
          <span className="text-[15px] font-semibold tracking-[-0.01em]">
            Gated
          </span>
        </a>

        <div className="flex flex-row items-center gap-3">
          <Link
            to="/sign-in"
            className="text-small text-fg-2 transition-colors hover:text-fg-1"
          >
            Sign in
          </Link>
          <Link
            to="/sign-up"
            onClick={() => track({ event: "public_scan_cta_clicked" })}
            className={buttonClasses("accent-strong", undefined, "sm")}
          >
            Audit your server for free
          </Link>
        </div>
      </div>
    </header>
  );
}
